import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import { loadPost, loadPostsByLang } from "./loader";
import type { LoadedPost } from "./types";
import { useLang } from "./useLang";
import { setMetaTags, setJsonLd } from "@/seo/seo";
import NewsletterForm from "@/components/newsletter/NewsletterForm";
import { Button } from "@/components/ui/button";

function slugify(input: string) {
  return input.toLowerCase().normalize('NFD').replace(/\p{Diacritic}/gu, '').replace(/[^a-z0-9\s-]/g, '').trim().replace(/\s+/g, '-').replace(/-+/g, '-');
}

type TocItem = { id: string; text: string; level: 2 | 3 };
function extractToc(md: string): TocItem[] {
  const lines = md.split(/\n+/);
  const toc: TocItem[] = [];
  for (const line of lines) {
    const m2 = /^##\s+(.+)$/.exec(line);
    const m3 = /^###\s+(.+)$/.exec(line);
    if (m2) toc.push({ id: slugify(m2[1]), text: m2[1].trim(), level: 2 });
    else if (m3) toc.push({ id: slugify(m3[1]), text: m3[1].trim(), level: 3 });
  }
  return toc;
}

function ReadProgress() {
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    const onScroll = () => {
      const h = document.documentElement;
      const scrolled = h.scrollTop;
      const height = h.scrollHeight - h.clientHeight;
      setProgress(height > 0 ? Math.min(100, Math.max(0, (scrolled / height) * 100)) : 0);
    };
    document.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => document.removeEventListener('scroll', onScroll);
  }, []);
  return (
    <div className="fixed top-0 left-0 right-0 h-1 bg-transparent z-40">
      <div className="h-full bg-gradient-to-r from-indigo-500 to-amber-500" style={{ width: `${progress}%` }} />
    </div>
  );
}

function TOC({ toc, title }: { toc: TocItem[]; title?: string }) {
  if (!toc.length) return null;
  return (
    <nav className="sticky top-24 hidden lg:block">
      <div className="rounded-2xl border border-primary/20 bg-gradient-to-br from-card via-primary/5 to-accent/5 p-6 shadow-lg backdrop-blur-sm">
        <div className="font-bold mb-6 text-base text-foreground">📑 {title || 'Table of contents'}</div>
        <ul className="space-y-4">
          {toc.map(item => (
            <li key={item.id} className={item.level === 3 ? 'ml-4 text-sm' : ''}>
              <a href={`#${item.id}`} className="text-foreground/70 hover:text-primary transition-colors hover:translate-x-1 inline-block leading-relaxed">
                {item.level === 2 ? '▸ ' : '◦ '}{item.text}
              </a>
            </li>
          ))}
        </ul>
      </div>
    </nav>
  );
}

function RelatedPosts({ current, lang }: { current: LoadedPost; lang: 'en'|'fr' }) {
  const [related, setRelated] = useState<LoadedPost[]>([]);
  
  useEffect(() => {
    loadPostsByLang(lang).then(all => {
      const tags = new Set(current.frontmatter.tags || []);
      const scored = all
        .filter(p => p.frontmatter.slug !== current.frontmatter.slug)
        .map(p => ({ 
          post: p, 
          score: (p.frontmatter.tags || []).reduce((acc, t) => acc + (tags.has(t) ? 1 : 0), 0) 
        }))
        .filter(x => x.score > 0)
        .sort((a,b) => {
          if (b.score !== a.score) return b.score - a.score;
          return new Date(b.post.frontmatter.date).getTime() - new Date(a.post.frontmatter.date).getTime();
        })
        .slice(0, 3)
        .map(x => x.post);
      setRelated(scored);
    });
  }, [current, lang]);

  if (!related.length) return null;

  return (
    <section className="mt-16">
      <h3 className="text-2xl font-bold mb-6 text-foreground">
        {lang==='fr' ? '📚 Articles liés' : '📚 Related articles'}
      </h3>
      <div className="grid sm:grid-cols-2 gap-6">
        {related.map(p => (
          <Link 
            key={p.frontmatter.slug} 
            to={`/blog/${p.frontmatter.slug}`} 
            className="group rounded-2xl border border-border bg-card p-6 hover:shadow-xl hover:border-primary/30 transition-all hover:-translate-y-1"
          >
            <div className="text-sm text-muted-foreground mb-2">
              {new Date(p.frontmatter.date).toLocaleDateString(lang === 'fr' ? 'fr-FR' : 'en-US')}
            </div>
            <div className="font-bold text-lg mb-2 text-foreground group-hover:text-primary transition-colors">
              {p.frontmatter.title}
            </div>
            <div className="text-sm text-muted-foreground line-clamp-2">
              {p.frontmatter.excerpt}
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}

export default function PostPage() {
  const { slug } = useParams();
  const { lang, setLang } = useLang();
  const [post, setPost] = useState<LoadedPost | null>(null);
  const [showHero, setShowHero] = useState(true);

  useEffect(() => { 
    if (slug) {
      console.log('PostPage loading post with slug:', slug, 'lang:', lang);
      loadPost(slug, lang).then(result => {
        console.log('Post loaded:', result);
        setPost(result);
      }).catch(err => {
        console.error('Error loading post:', err);
      });
    }
  }, [slug, lang]);
  
  const toc = useMemo(() => extractToc(post?.content || ''), [post]);

  const { canonicalUrl, ogLocale } = useMemo(() => {
    const base = window.location.origin;
    const url = `${base}/blog/${slug ?? ""}`;
    return { canonicalUrl: url, ogLocale: lang === "fr" ? "fr_FR" : "en_US" };
  }, [slug, lang]);

  useEffect(() => {
    if (!post) return;
    const fm = post.frontmatter;
    const title = fm.title + (lang === "fr" ? " | Blog AcquizGroup" : " | AcquizGroup Blog");
    const description = fm.excerpt || "";
    const image = fm.hero || undefined;

    setMetaTags({
      title,
      description,
      url: canonicalUrl,
      image,
      locale: ogLocale,
      type: "article",
    });

    const jsonLd = {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: fm.title,
      inLanguage: lang,
      datePublished: fm.date,
      dateModified: fm.date,
      author: [{ '@type': 'Organization', name: 'AcquizGroup' }],
      publisher: { '@type': 'Organization', name: 'AcquizGroup' },
      mainEntityOfPage: canonicalUrl,
      image: image ? [image] : undefined,
      description,
    };
    setJsonLd(jsonLd);
  }, [post, canonicalUrl, ogLocale, lang]);

  if (!post) {
    return (
      <div className="mx-auto max-w-3xl p-6 min-h-screen bg-background">
        <ReadProgress />
        <div className="mb-6 flex items-center justify-between">
          <Link to="/blog" className="underline text-primary">
            ← {lang === "en" ? "Back to all" : "Retour"}
          </Link>
          <Button variant="outline" onClick={() => setLang(lang === "en" ? "fr" : "en")}>
            {lang === "en" ? "FR" : "EN"}
          </Button>
        </div>
        <p className="text-muted-foreground">
          {lang === "en" ? "Loading…" : "Chargement…"}
        </p>
      </div>
    );
  }

  const { frontmatter } = post;
  
  const components = {
    h2: (props: any) => { 
      const txt = String(props.children?.[0] ?? ''); 
      const id = slugify(txt); 
      return <h2 id={id} className="font-extrabold mt-10 mb-5" {...props} />; 
    },
    h3: (props: any) => { 
      const txt = String(props.children?.[0] ?? ''); 
      const id = slugify(txt); 
      return <h3 id={id} className="font-bold" {...props} />; 
    }
  } as any;

  return (
    <>
      <ReadProgress />
      <div className="bg-gradient-to-b from-background via-secondary/5 to-background min-h-screen">
        <div className="mx-auto max-w-6xl p-6 grid lg:grid-cols-[1fr_280px] gap-8">
          <article className="prose prose-lg prose-neutral dark:prose-invert max-w-none">
            <div className="mb-8 flex items-center justify-between">
              <Link to="/blog" className="inline-flex items-center gap-2 text-primary hover:text-primary/80 transition-colors font-medium">
                ← {lang === "en" ? "Back to all posts" : "Retour aux articles"}
              </Link>
              <Button variant="outline" size="sm" onClick={() => setLang(lang === "en" ? "fr" : "en")}>
                {lang === "en" ? "🇫🇷 FR" : "🇬🇧 EN"}
              </Button>
            </div>
            
            <div className="mb-8">
              <h1 className="text-5xl font-bold mb-4 text-foreground leading-tight bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
                {frontmatter.title}
              </h1>
              <div className="flex items-center gap-4 text-muted-foreground text-sm">
                <time dateTime={frontmatter.date}>
                  {new Date(frontmatter.date).toLocaleDateString(lang === 'fr' ? 'fr-FR' : 'en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </time>
                {frontmatter.tags && frontmatter.tags.length > 0 && (
                  <div className="flex gap-2">
                    {frontmatter.tags.map(tag => (
                      <span key={tag} className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-primary/10 text-primary border border-primary/20">
                        {tag}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
            
              {frontmatter.hero && showHero && (
                <div className="relative rounded-2xl overflow-hidden shadow-2xl my-8 aspect-video">
                  <img 
                    src={frontmatter.hero} 
                    alt={frontmatter.title} 
                    className="w-full h-full object-cover"
                    onError={() => setShowHero(false)}
                  />
                </div>
              )}

            {/* Inline TOC (mobile) */}
            {toc.length > 0 && (
              <div className="rounded-2xl border border-primary/20 bg-gradient-to-br from-primary/5 to-accent/5 p-6 my-8 lg:hidden backdrop-blur-sm">
                <div className="font-bold mb-6 text-lg text-foreground">{lang==='fr'?'📑 Sommaire':'📑 Table of contents'}</div>
                <ul className="space-y-4">
                  {toc.map(item => (
                    <li key={item.id} className={item.level === 3 ? 'ml-4 text-sm' : ''}>
                      <a href={`#${item.id}`} className="text-foreground/80 hover:text-primary transition-colors hover:translate-x-1 inline-block leading-relaxed">
                        {item.level === 2 ? '▸ ' : '◦ '}{item.text}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="font-serif text-lg leading-relaxed prose-headings:font-bold prose-h2:text-3xl prose-h2:mt-16 prose-h2:mb-8 prose-h2:text-foreground prose-h2:font-extrabold prose-h3:text-xl prose-h3:mt-12 prose-h3:mb-6 prose-h3:font-bold prose-p:text-foreground/90 prose-p:leading-[1.8] prose-p:mb-10 prose-p:text-lg prose-strong:text-foreground prose-strong:font-semibold prose-ul:my-8 prose-li:text-foreground/90 prose-li:my-3 prose-a:text-primary prose-a:no-underline hover:prose-a:underline prose-blockquote:border-l-4 prose-blockquote:border-primary prose-blockquote:bg-primary/5 prose-blockquote:py-4 prose-blockquote:px-6 prose-blockquote:rounded-r-lg prose-blockquote:italic prose-blockquote:text-foreground/80">
              <ReactMarkdown components={components}>{post.content}</ReactMarkdown>
            </div>

          {/* Newsletter below article */}
          <div className="mt-16 p-8 rounded-2xl bg-gradient-to-br from-primary/10 via-accent/10 to-primary/5 border border-primary/20">
            <h3 className="text-2xl font-bold mb-4 text-foreground">
              {lang==='fr' ? '📬 Recevez nos prochains articles' : '📬 Get our next posts'}
            </h3>
            <p className="text-muted-foreground mb-6">
              {lang==='fr' 
                ? 'Restez informé des dernières tendances en acquisition d\'entreprise' 
                : 'Stay informed about the latest trends in business acquisition'}
            </p>
            <NewsletterForm 
              lang={lang} 
              placeholder={lang==='fr'?"Votre adresse email":"Your email"} 
              cta={lang==='fr'?"S'abonner":"Subscribe"} 
            />
          </div>

          <RelatedPosts current={post} lang={lang} />
        </article>

        <TOC toc={toc} title={lang==='fr'?'Sommaire':'Contents'} />
        </div>
      </div>
    </>
  );
}
